/**
 * Lazy Loading Components
 * Implements React.lazy for better code splitting and performance
 */

import React, { Suspense } from 'react';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useBootstrapTheme } from '../contexts/BootstrapThemeContext';

// Lazy load heavy components
export const LazyBootstrapInterfaceBuilder = React.lazy(() => 
  import('../pages/NewPages/BootstrapInterfaceBuilder').then(module => ({
    default: module.BootstrapInterfaceBuilder
  }))
);

export const LazyWorkflowEditorPage = React.lazy(() => 
  import('../pages/NewPages/WorkflowEditorPage').then(module => ({
    default: module.WorkflowEditorPage
  }))
);

export const LazyBootstrapAnalytics = React.lazy(() => 
  import('../pages/NewPages/BootstrapAnalytics').then(module => ({
    default: module.BootstrapAnalytics
  }))
);

// Loading fallback component with dark mode support
export const ComponentLoadingFallback = ({ name }: { name?: string }) => {
  const { effectiveTheme } = useBootstrapTheme();
  
  return (
    <div 
      className="d-flex align-items-center justify-content-center"
      style={{ 
        height: '100vh',
        background: effectiveTheme === 'dark' 
          ? 'linear-gradient(135deg, #0d1117 0%, #161b22 50%, #0d1117 100%)'
          : 'linear-gradient(135deg, #f9fafb 0%, #ffffff 50%, #f9fafb 100%)'
      }}
    >
      <div 
        className={`text-center p-5 rounded-4 border shadow-sm ${
          effectiveTheme === 'dark' 
            ? 'text-light' 
            : ''
        }`}
        style={{ 
          backdropFilter: 'blur(10px)',
          backgroundColor: effectiveTheme === 'dark' 
            ? 'rgba(13, 17, 23, 0.95)' 
            : 'rgba(255, 255, 255, 0.9)',
          borderColor: effectiveTheme === 'dark' 
            ? 'rgba(48, 54, 61, 0.8)' 
            : 'rgba(229, 231, 235, 0.5)'
        }}
      >
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>
          {name === 'executions' && '🚀'}
          {name === 'editor' && '🏗️'}
          {name === 'interface-builder' && '🎨'}
          {name === 'analytics' && '📊'}
          {!name && '⚙️'}
        </div>
        <LoadingSpinner size="lg" />
        <p className={`mt-3 mb-0 ${effectiveTheme === 'dark' ? 'text-light opacity-75' : 'text-muted'}`}>
          Loading {name || 'component'}...
        </p>
      </div>
    </div>
  );
};

// Higher-order component for consistent lazy loading
export const withLazyLoading = <P extends object>(
  LazyComponent: React.LazyExoticComponent<React.ComponentType<P>>,
  fallbackName?: string
) => {
  return (props: P) => (
    <Suspense fallback={<ComponentLoadingFallback name={fallbackName} />}>
      <LazyComponent {...props} />
    </Suspense>
  );
};

// Pre-configured lazy components with loading states
export const WorkflowEditorPageWithLoading = withLazyLoading(LazyWorkflowEditorPage, 'editor');
export const InterfaceBuilderWithLoading = withLazyLoading(LazyBootstrapInterfaceBuilder, 'interface-builder');
export const AnalyticsWithLoading = withLazyLoading(LazyBootstrapAnalytics, 'analytics');